import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, text, tip, warning } from './authoring.ts';

/** Measurement and geometry — where arithmetic meets the physical world. */
export const GEOMETRY_LESSONS: Lesson[] = [
  lesson({
    id: 'perimeter-01',
    unitId: 'unit-measurement',
    conceptId: 'perimeter',
    title: 'Perimeter',
    summary: 'The distance all the way around a shape — a walk along the edge.',
    objectives: ['Find the perimeter of a polygon', 'Use the shortcut formula for a rectangle', 'Find a missing side given the perimeter'],
    estimatedMinutes: 20,
    sections: {
      introduction: {
        title: 'A walk around the edge',
        blocks: [
          text('You want to put a fence around a garden. How much fence do you need? That is the perimeter: the total distance around the outside.'),
          text('It is a length, so it is measured in metres, centimetres or feet — never in square units.'),
        ],
      },
      intuition: {
        title: 'Just add up the sides',
        blocks: [
          text('There is no clever idea here. Walk around the shape and add each side as you pass it. For a rectangle, opposite sides are equal, which gives a shortcut.'),
          math('P = 2(\\ell + w)'),
          insight('The formula is only the addition done efficiently. If you forget it, adding all four sides always works.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [figure({ kind: 'shape', shape: 'rectangle', labels: { width: '8 cm', height: '5 cm' } }, 'Walking around: 8 + 5 + 8 + 5.')],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A rectangle is 8 cm long and 5 cm wide. Find its perimeter.'),
          steps([
            { label: 'Add all four sides.', latex: '8 + 5 + 8 + 5' },
            { label: 'Or use the shortcut.', latex: 'P = 2(8 + 5) = 2 \\times 13' },
            { label: 'Answer, with units.', latex: '26 \\text{ cm}' },
          ]),
          warning('Perimeter is not area. Perimeter measures the edge, in cm. Area measures the inside, in cm². Mixing them up is the single most common mistake in this whole topic.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('perimeter', 4)] },
      independent: { title: 'On your own', blocks: [practice('perimeter', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('perimeter', 3, 'Work backwards from the perimeter to a missing side.')] },
      review: {
        title: 'What to remember',
        blocks: [list(['Perimeter is the distance around the outside.', 'Add every side.', 'Rectangle shortcut: 2 × (length + width).', 'Units are lengths: cm, m, km.'])],
      },
    },
  }),

  lesson({
    id: 'area-rectangles-01',
    unitId: 'unit-measurement',
    conceptId: 'area-rectangles',
    title: 'Area of Rectangles',
    summary: 'How many unit squares fit inside — and why that turns out to be length times width.',
    objectives: ['Find the area of a rectangle by counting and by formula', 'Explain why the formula works', 'Distinguish area from perimeter'],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Covering the floor',
        blocks: [
          text('You want to tile a floor. How many tiles? That is area: how much surface there is to cover.'),
          text('Area is measured in *square* units — square centimetres, square metres — because the thing being counted is squares.'),
        ],
      },
      intuition: {
        title: 'Why length times width',
        blocks: [
          text('Cover a rectangle with 1 cm squares. They land in neat rows: each row has as many squares as the rectangle is long, and there are as many rows as it is wide.'),
          text('So the count is rows × per-row — which is exactly the multiplication array you already know.'),
          math('A = \\ell \\times w'),
          insight('The area formula is the multiplication array from Grade 3, wearing a ruler. That is why area belongs to multiplication and perimeter belongs to addition.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'grid', width: 8, height: 5 }, 'Eight squares in each row, five rows: 40 squares.'),
          figure({ kind: 'array', rows: 5, columns: 8, label: '5 × 8 = 40' }),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A rectangle is 8 cm long and 5 cm wide. Find its area.'),
          steps([
            { label: 'Multiply length by width.', latex: 'A = 8 \\times 5' },
            { label: 'Compute.', latex: '40' },
            { label: 'Answer, with square units.', latex: '40 \\text{ cm}^2' },
          ]),
          tip('Same rectangle as the perimeter lesson: perimeter 26 cm, area 40 cm². Different questions, different answers, different units. Always ask yourself which one the problem wants.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('area-rectangles', 4)] },
      independent: { title: 'On your own', blocks: [practice('area-rectangles', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('area-rectangles', 3, 'Find a missing side from the area, or the area of an L-shape made from two rectangles.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list(['Area is the number of unit squares inside.', 'Rectangle: area = length × width.', 'Units are squared: cm², m².', 'A composite shape can be split into rectangles.']),
        ],
      },
    },
  }),
];
